/**
 * PATH: packages/ui/src/components/settings/EmailTab.tsx
 * PURPOSE: Manage email preferences (signature, digest, sender defaults)
 *
 * WHY: Users need consistent email behavior across hubs without leaving the
 *      universal settings modal.
 *
 * FLOW:
 *   ┌─────────────┐    ┌──────────────┐    ┌──────────────┐
 *   │ Load Prefs  │───▶│ Edit Fields  │───▶│ Save Updates │
 *   └─────────────┘    └──────────────┘    └──────────────┘
 *
 * DEPENDENCIES:
 *   - lucide-react: icons
 *   - SettingsApiClient: API contract for persistence
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AtSign, Mail, Loader2, Check } from 'lucide-react'
import { SignatureField } from '@quotech/tenant-shared/settings/SignatureField'
import {
  loadCachedSignatureHtml,
  fetchDurableSignatureHtml,
  saveSignatureHtml,
  signatureHtmlToPlainText,
  plainTextToSignatureHtml,
} from '@quotech/tenant-shared/settings/emailSignature'
import { QuotechBrandingFooterPreview } from '@quotech/tenant-shared/settings/QuotechBrandingFooterPreview'
import { cn } from '../../lib/utils'
import {
  setUndoSendSeconds,
  normaliseUndoSendSeconds,
  UNDO_SEND_DELAY_OPTIONS,
  DEFAULT_UNDO_SEND_SECONDS,
} from '../../lib/undoSendPref'
import { setQuotechBrandingDefault } from '../../lib/brandingPref'
import type { SettingsApiClient } from './types'
import { LabelPreferencesSection } from './email-label-prefs/LabelPreferencesSection'
import { SenderProfilesSection } from './sender-profiles/SenderProfilesSection'

export interface EmailPreferences {
  sender_name: string
  reply_to: string
  signature: string
  digest_frequency: 'none' | 'daily' | 'weekly'
  auto_cc: boolean
  delivery_failure_alerts: boolean
  // "Powered by Quotech" GTM footer — OPT-IN (FEAT-2026-07-07). false
  // (default) sends without the marketing footer; true adds it below the
  // signature. The composer shows a removable chip whenever it will ship.
  quotech_branding: boolean
  // Composer "undo send" grace window in seconds (0 = send immediately).
  // Persisted server-side so it follows the user across devices; mirrored to
  // a localStorage cache so the composer can read it synchronously.
  undo_send_seconds: number
}

export interface EmailTabProps {
  preferences?: Partial<EmailPreferences>
  apiClient?: SettingsApiClient
  endpoint?: string
  isLoading?: boolean
  isSaving?: boolean
  onSave?: (prefs: EmailPreferences) => Promise<void>
  className?: string
}

interface ToggleProps {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}

function Toggle({ checked, onChange, disabled }: ToggleProps) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      disabled={disabled}
      className={cn(
        'relative w-11 h-6 rounded-full transition-colors',
        checked ? 'bg-teal-500' : 'bg-slate-300 dark:bg-slate-600',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      <span
        className={cn(
          'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform',
          checked && 'translate-x-5'
        )}
      />
    </button>
  )
}

export function EmailTab({
  preferences: initialPrefs,
  apiClient,
  // NOTE: apiClient baseURL already includes /api, so endpoint should NOT include /api prefix
  endpoint = '/settings/email/',
  isLoading = false,
  isSaving = false,
  onSave,
  className,
}: EmailTabProps) {
  const defaultPreferences = useMemo<EmailPreferences>(() => ({
    sender_name: '',
    reply_to: '',
    signature: '',
    digest_frequency: 'weekly',
    auto_cc: false,
    delivery_failure_alerts: true,
    quotech_branding: false,
    undo_send_seconds: DEFAULT_UNDO_SEND_SECONDS,
    ...initialPrefs,
  }), [initialPrefs])

  const [prefs, setPrefs] = useState<EmailPreferences>(defaultPreferences)
  const [remoteLoading, setRemoteLoading] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  // The signature is no longer stored in `prefs` — it lives in the ONE
  // canonical store (@quotech/tenant-shared/settings/emailSignature) that
  // the composer/send path actually reads, so what you set up here lands
  // on sent mail. `prefs.signature` is kept as a best-effort plain-text
  // mirror for backward compatibility only.
  const [signatureHtml, setSignatureHtml] = useState<string>('')
  // Flips once the canonical store yields a value, so the legacy-field
  // migration below doesn't clobber a real signature.
  const signatureSeededRef = useRef(false)

  useEffect(() => {
    if (!initialPrefs) return
    setPrefs(prev => ({ ...prev, ...initialPrefs }))
  }, [initialPrefs])

  // Keep the localStorage cache in lock-step with the persisted (DB) value so
  // the composer — which reads the cache synchronously when it mounts — always
  // reflects the saved choice. Source of truth is the backend; this is just a
  // fast mirror. (The composer also self-heals via a background fetch.)
  useEffect(() => {
    setUndoSendSeconds(prefs.undo_send_seconds)
  }, [prefs.undo_send_seconds])

  // Same lock-step mirror for the "Powered by Quotech" footer default so the
  // composer's footer chip matches the saved (opt-in) choice immediately on
  // this device (FEAT-2026-07-07-GTM-FOOTER-OPT-IN).
  useEffect(() => {
    setQuotechBrandingDefault(prefs.quotech_branding === true)
  }, [prefs.quotech_branding])

  // Hydrate the signature editor from the canonical store: instant cache
  // first, then the durable per-user/per-org value.
  useEffect(() => {
    const cached = loadCachedSignatureHtml()
    if (cached) {
      setSignatureHtml(cached)
      signatureSeededRef.current = true
    }
    let mounted = true
    void fetchDurableSignatureHtml().then((durable) => {
      if (!mounted) return
      if (durable) {
        setSignatureHtml(durable)
        signatureSeededRef.current = true
      }
    })
    return () => { mounted = false }
  }, [])

  // One-time migration: an operator who only ever set the legacy plain
  // `signature` field (before unification) keeps it — seed the editor
  // from it when the canonical store is empty.
  useEffect(() => {
    if (signatureSeededRef.current) return
    if (prefs.signature && !signatureHtml) {
      setSignatureHtml(plainTextToSignatureHtml(prefs.signature))
    }
  }, [prefs.signature, signatureHtml])

  useEffect(() => {
    if (!apiClient) return
    let isMounted = true
    setRemoteLoading(true)
    apiClient.get(endpoint)
      .then((response) => {
        if (!isMounted) return
        const data = response?.data ?? response
        const serverPrefs = data?.preferences ?? data
        if (serverPrefs) {
          setPrefs(prev => ({ ...prev, ...serverPrefs }))
          setHasChanges(false)
        }
      })
      .catch(() => {
        if (!isMounted) return
      })
      .finally(() => {
        if (!isMounted) return
        setRemoteLoading(false)
      })
    return () => {
      isMounted = false
    }
  }, [apiClient, endpoint])

  const handleChange = useCallback((key: keyof EmailPreferences, value: EmailPreferences[keyof EmailPreferences]) => {
    setPrefs(prev => ({ ...prev, [key]: value }))
    setHasChanges(true)
  }, [])

  const handleSave = useCallback(async () => {
    // Persist the signature to the canonical store (what the send path
    // reads) and mirror a plain-text copy onto the legacy prefs field.
    saveSignatureHtml(signatureHtml)
    const prefsToSave: EmailPreferences = {
      ...prefs,
      signature: signatureHtmlToPlainText(signatureHtml),
    }
    if (onSave) {
      await onSave(prefsToSave)
      setHasChanges(false)
      return
    }
    if (apiClient?.put) {
      await apiClient.put(endpoint, { preferences: prefsToSave })
      setHasChanges(false)
    }
  }, [apiClient, endpoint, onSave, prefs, signatureHtml])

  if (isLoading || remoteLoading) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    )
  }

  return (
    <div className={cn('space-y-6 max-w-2xl', className)}>
      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Email Preferences</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Control how Quotech sends emails on your behalf.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Sender name</label>
          <div className="relative mt-1">
            <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={prefs.sender_name}
              onChange={(e) => handleChange('sender_name', e.target.value)}
              placeholder="e.g., Alex from Quotech"
              className="w-full pl-9 pr-3 py-2 rounded-lg text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Reply-to address</label>
          <div className="relative mt-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="email"
              value={prefs.reply_to}
              onChange={(e) => handleChange('reply_to', e.target.value)}
              placeholder="reply@quotech.ai"
              className="w-full pl-9 pr-3 py-2 rounded-lg text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Signature</label>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Added to every new message, reply and forward. Paste a formatted
            signature or type one - it follows you across devices.
          </p>
          <SignatureField
            className="mt-1"
            valueHtml={signatureHtml}
            onChangeHtml={(html) => { setSignatureHtml(html); setHasChanges(true) }}
            placeholder={'Best regards,\nAlex from Quotech'}
          />
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Digest frequency</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">How often to send your activity digest.</p>
          </div>
          <select
            value={prefs.digest_frequency}
            onChange={(e) => handleChange('digest_frequency', e.target.value as EmailPreferences['digest_frequency'])}
            className="px-3 py-2 rounded-lg text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          >
            <option value="none">Off</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Undo send</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Briefly hold a message after you hit Send so you can still hit “Undo”.
              Choose Off to send immediately. Save to sync across your devices.
            </p>
          </div>
          <select
            value={String(prefs.undo_send_seconds)}
            onChange={(e) => handleChange('undo_send_seconds', normaliseUndoSendSeconds(e.target.value))}
            className="px-3 py-2 rounded-lg text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            aria-label="Undo send delay"
          >
            {UNDO_SEND_DELAY_OPTIONS.map((opt) => (
              <option key={opt.seconds} value={opt.seconds}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Auto CC me</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Automatically CC your email on replies.</p>
          </div>
          <Toggle checked={prefs.auto_cc} onChange={(checked) => handleChange('auto_cc', checked)} />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Delivery failure alerts</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Get notified if an email fails.</p>
          </div>
          <Toggle
            checked={prefs.delivery_failure_alerts}
            onChange={(checked) => handleChange('delivery_failure_alerts', checked)}
          />
        </div>
      </div>

      {/* "Powered by Quotech" footer — OPT-IN + live preview
          (FEAT-2026-07-07-GTM-FOOTER-OPT-IN). The footer never ships
          unless this is turned ON; `=== true` treats a missing value as
          OFF to match the backend opt-in default. When ON, the composer
          also shows a removable chip on every email before it sends. */}
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Add “Powered by Quotech” footer to my emails</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Off by default - nothing is added to your emails unless you turn
              this on. When on, a small branded footer is added below your
              signature, and the composer shows it on each email so you can
              remove it for a single send.
            </p>
          </div>
          <Toggle
            checked={prefs.quotech_branding === true}
            onChange={(checked) => handleChange('quotech_branding', checked)}
          />
        </div>
        <div>
          <p className="mb-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">
            {prefs.quotech_branding === true
              ? 'Preview - added below your signature on sent emails:'
              : 'Preview - this footer will NOT be added to your emails:'}
          </p>
          <QuotechBrandingFooterPreview enabled={prefs.quotech_branding === true} />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={isSaving || !hasChanges}
          className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
        </button>
        {!isSaving && !hasChanges && (
          <span className="text-xs text-slate-400 flex items-center gap-1">
            <Check className="w-4 h-4" /> Saved
          </span>
        )}
      </div>

      {/* Two-tier labelling preferences: Personal (private) -> Organisation
          (shared). Self-loads from /api/email/label-preferences/. */}
      <LabelPreferencesSection apiClient={apiClient} />

      {/* W3-V8-P3 — counterparty stewardship: what the AI has learned each
          sender IS (Supplier/Client/...), with accept/correct/reject.
          Self-loads from /api/kanban/senders/. */}
      <SenderProfilesSection apiClient={apiClient} />
    </div>
  )
}

export default EmailTab
